import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Facebook, Twitter, Instagram, Linkedin, Mail, Phone, MapPin, Clock, ArrowUpRight } from 'lucide-react';
import dulogo from '@/assets/logo.png';

const Footer = () => {
  const { t } = useTranslation();

  const links = [
    { label: t('home'), to: '/' },
    { label: 'Book Appointments', to: '/appointments/new' },
    { label: 'Track Application', to: '/track-application' },
    { label: t('grievances'), to: '/grievances' },
    { label: 'Optional Services', to: '/optional-services' },
    { label: 'About Us', to: 'https://dudigitalglobal.com/about-us/',},
    { label: 'FAQs', to: '/faqs' },
    { label: 'Holiday List', to: '/holiday-list' },
  ];

  const hours = [
    { label: 'Working Hours', value: '__' },
    { label: 'Submission Hours', value: '__' },
    { label: 'Walk-in Time', value: '__' },
    { label: 'Lunch Break', value: '__' },
  ];

  const socials = [
    { icon: Facebook, label: 'Facebook', href: '#' },
    { icon: Twitter, label: 'Twitter', href: '#' },
    { icon: Instagram, label: 'Instagram', href: '#' },
    { icon: Linkedin, label: 'LinkedIn', href: '#' },
  ];

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Lora:wght@400;500;600&family=DM+Sans:wght@300;400;500;600&display=swap');

        .footer-root {
          background: #0f1f2e;
          color: #d9e8f0;
          font-family: 'DM Sans', sans-serif;
          position: relative;
          overflow: hidden;
          
        }

        .footer-root::before {
          content: '';
          position: absolute;
          top: 0; left: 0; right: 0;
          height: 2px;
          background: linear-gradient(90deg, transparent, #c9a84c, #e8c96d, #c9a84c, transparent);
        }

        .footer-bg-circle {
          position: absolute;
          border-radius: 50%;
          background: radial-gradient(circle, rgba(201,168,76,0.06) 0%, transparent 70%);
          pointer-events: none;
        }

        .footer-inner {
          max-width: 1200px;
          margin: 0 auto;
          padding: 25px 30px 10px;
          position: relative;
          z-index: 1;
         
        }

        .footer-grid {
          display: grid;
          grid-template-columns: 1.4fr 1fr 1fr 1.3fr;
          gap: 48px;
        }

        @media (max-width: 1024px) {
          .footer-grid { grid-template-columns: 1fr 1fr; gap: 36px; }
        }
        @media (max-width: 600px) {
          .footer-grid { grid-template-columns: 1fr; gap: 28px; }
          .footer-inner { padding: 40px 20px 28px; }
        }

        /* Brand col */
        .footer-brand-logo {
          height: 80px;
          width: auto;
        }

        .footer-brand-tagline {
          font-family: 'Lora', serif;
          font-size: 13px;
          font-style: italic;
          color: #8aadbe;
          line-height: 1.6;
          max-width: 220px;
          margin-bottom: 20px;
        }

        .footer-socials {
          display: flex;
          gap: 10px;
        }

        .footer-social-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 36px; height: 36px;
          border-radius: 8px;
          background: rgba(255,255,255,0.06);
          border: 1px solid rgba(255,255,255,0.08);
          color: #8aadbe;
          transition: all 0.2s ease;
          text-decoration: none;
        }

        .footer-social-btn:hover {
          background: rgba(201,168,76,0.15);
          border-color: rgba(201,168,76,0.4);
          color: #e8c96d;
          transform: translateY(-2px);
        }

        /* Section heading */
        .footer-section-title {
          font-family: 'DM Sans', sans-serif;
          font-size: 11px;
          font-weight: 600;
          letter-spacing: 0.12em;
          text-transform: uppercase;
          color: #c9a84c;
          margin-bottom: 20px;
          padding-bottom: 10px;
          border-bottom: 1px solid rgba(201,168,76,0.2);
        }

        /* Links col */
        .footer-links {
          list-style: none;
          padding: 0; margin: 0;
          display: flex;
          flex-direction: column;
          gap: 10px;
        }

        .footer-links a {
          font-size: 13.5px;
          color: #9ab9c8;
          text-decoration: none;
          display: inline-flex;
          align-items: center;
          gap: 4px;
          transition: color 0.2s ease;
          font-weight: 400;
        }

        .footer-links a:hover {
          color: #e8c96d;
        }

        .footer-links a .link-arrow {
          opacity: 0;
          transform: translateX(-4px);
          transition: all 0.2s ease;
        }

        .footer-links a:hover .link-arrow {
          opacity: 1;
          transform: translateX(0);
        }

        /* Contact col */
        .footer-contact-item {
          margin-bottom: 14px;
        }

        .footer-contact-label {
          font-size: 10.5px;
          font-weight: 600;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          color: #546e7a;
          margin-bottom: 2px;
        }

        .footer-contact-value {
          font-size: 13px;
          color: #aac8d6;
          line-height: 1.5;
        }

        .footer-contact-value a {
          color: #aac8d6;
          text-decoration: none;
          transition: color 0.2s;
        }

        .footer-contact-value a:hover { color: #e8c96d; }

        .footer-company-name {
          font-size: 13px;
          font-weight: 600;
          color: #d9e8f0;
          letter-spacing: 0.02em;
          margin-bottom: 18px;
        }

        /* Hours col */
        .footer-hours-item {
          display: flex;
          flex-direction: column;
          gap: 2px;
          padding: 10px 12px;
          border-radius: 8px;
          background: rgba(255,255,255,0.03);
          border: 1px solid rgba(255,255,255,0.05);
          margin-bottom: 8px;
          transition: background 0.2s;
        }

        .footer-hours-item:hover {
          background: rgba(201,168,76,0.06);
          border-color: rgba(201,168,76,0.15);
        }

        .footer-hours-label {
          font-size: 10.5px;
          font-weight: 600;
          letter-spacing: 0.07em;
          text-transform: uppercase;
          color: #546e7a;
        }

        .footer-hours-value {
          font-size: 13px;
          color: #aac8d6;
        }

        /* Divider + bottom bar */
        .footer-divider {
          border: none;
          border-top: 1px solid rgba(255,255,255,0.07);
        }

        .footer-bottom {
          max-width: 1200px;
          margin: 0 auto;
          padding: 18px 32px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 16px;
          position: relative;
          z-index: 1;
         
        }

        @media (max-width: 600px) {
          .footer-bottom { flex-direction: column; text-align: center; padding: 16px 20px; }
        }

        .footer-copyright {
          font-size: 12px;
          color: #4a6475;
          letter-spacing: 0.02em;
        }

        .footer-bottom-badge {
          font-size: 11px;
          color: #4a6475;
          display: flex;
          align-items: center;
          gap: 6px;
        }

        .footer-bottom-badge span {
          width: 6px; height: 6px;
          border-radius: 50%;
     
          display: inline-block;
        
        
        }

        @keyframes pulse-dot {
          0%   { box-shadow: 0 0 0 0 rgba(76,175,119,0.4); }
          70%  { box-shadow: 0 0 0 6px rgba(76,175,119,0); }
          100% { box-shadow: 0 0 0 0 rgba(76,175,119,0); }
        }
      `}</style>

      <footer className="footer-root">
        {/* Decorative bg circles */}
        <div className="footer-bg-circle" style={{ width: 500, height: 500, top: -200, right: -100 }} />
        <div className="footer-bg-circle" style={{ width: 300, height: 300, bottom: -100, left: -50 }} />

        <div className="footer-inner">
          <div className="footer-grid">

            {/* ── Brand ── */}
            <div>
              <img src={dulogo} alt="DU Digital Global" className="footer-brand-logo" />
              <p className="footer-brand-tagline">
               Follow Us Here:
              </p>
              <div className="footer-socials">
                {socials.map(({ icon: Icon, label, href }) => (
                  <a key={label} href={href} className="footer-social-btn" aria-label={label}>
                    <Icon size={15} />
                  </a>
                ))}
              </div>
            </div>

            {/* ── Quick Links ── */}
            <div>
              <p className="footer-section-title">Quick Links</p>
              <ul className="footer-links">
                {links.map(({ label, to }) => (
                  <li key={to}>
                    <Link to={to}>
                      {label}
                      <ArrowUpRight size={12} className="link-arrow" />
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* ── Contact ── */}
            <div>
              <p className="footer-section-title">Contact Us</p>
              <p className="footer-company-name">DUDigital Global Limited</p>

              <div className="footer-contact-item">
                <p className="footer-contact-label">Email</p>
                <p className="footer-contact-value">
                    icac.singapore@dudigitalglobal.com
                </p>
              </div>

              <div className="footer-contact-item">
                <p className="footer-contact-label">Helpline</p>
                <p className="footer-contact-value">
                +65 6XXX XXXX
                </p>
              </div>

              <div className="footer-contact-item">
                <p className="footer-contact-label">Address</p>
                <p className="footer-contact-value">
                  Floor 3rd, 298 Jalan Besar<br />Singapore 208959
                </p>
              </div>
            </div>

            {/* ── Hours ── */}
            <div>
              <p className="footer-section-title">Office Hours</p>
              {hours.map(({ label, value }) => (
                <div key={label} className="footer-hours-item">
                  <span className="footer-hours-label">{label}</span>
                  <span className="footer-hours-value">{value}</span>
                </div>
              ))}
            </div>

          </div>
        </div>

        <hr className="footer-divider" />

        <div className="footer-bottom">
          <p className="footer-copyright">{t('footerCopyright')}</p>
          <p className="footer-bottom-badge">
            <span /> Terms and Conditions & Privacy Policy
          </p>
        </div>
      </footer>
    </>
  );
};

export default Footer;